import { useIonAlert } from '@ionic/react';
import { useCampaignStore } from '@lidea/shared/data-access/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateSyncJobParams } from '../sync-job/create-sync-job';
import {
  clearOfflineCache,
  clearSkipAutoSyncSession,
} from '../sync-job/clear-offline-cache';
import { isOfflineSyncDone } from '../sync-job/sync-job-store';
import { useSyncJobContext } from '../sync-job/sync-job-provider';
import { isResumableSyncPhase } from '../sync-job/types';

import classes from './use-setup-cache.module.css';

export const useSetupCache = () => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const [presentAlert] = useIonAlert();
  const queryClient = useQueryClient();
  const campaignId = useCampaignStore((s) => s.campaignId);
  const {
    job,
    progress,
    isRunning,
    runJob,
    resumeJob,
    pauseJob,
    refreshJob,
    cancelJob,
  } = useSyncJobContext();
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);

  const getPhaseMessage = useCallback(() => {
    if (!progress) {
      return t('common:sync_job.phase_listing');
    }
    switch (progress.phase) {
      case 'listing':
        return t('common:sync_job.phase_listing');
      case 'plots':
        return t('common:sync_job.phase_plots', {
          percent: progress.plotsPercent,
          done: progress.completedPlots,
          total: progress.totalPlots,
          remaining: progress.remainingPlots,
        });
      case 'analyses':
        return t('common:sync_job.phase_analyses', {
          percent: progress.analysesPercent,
          done: progress.completedAnalyses,
          total: progress.totalAnalyses,
          remaining: progress.remainingAnalyses,
        });
      case 'heatmaps':
        return t('common:sync_job.phase_heatmaps', {
          percent: progress.heatmapsPercent,
          done: progress.completedHeatmaps,
          total: progress.totalHeatmaps,
          remaining: progress.remainingHeatmaps,
        });
      default:
        return null;
    }
  }, [progress, t]);

  const startSyncWithParams = useCallback(
    async (params: CreateSyncJobParams) => {
      if (!campaignId) {
        return { setupCompleted: false };
      }
      const result = await runJob({ ...params, campaignId });
      return {
        setupCompleted:
          result.status === 'completed' ||
          result.status === 'completed_with_errors',
      };
    },
    [campaignId, runJob]
  );

  const startAutoSync = useCallback(
    async (params: CreateSyncJobParams) => {
      clearSkipAutoSyncSession();
      return startSyncWithParams({ ...params, includeHeatmaps: false });
    },
    [startSyncWithParams]
  );

  const startSyncWithImages = useCallback(
    async (params: CreateSyncJobParams) => {
      clearSkipAutoSyncSession();
      return startSyncWithParams({ ...params, includeHeatmaps: true });
    },
    [startSyncWithParams]
  );

  const isScopeAlreadySynced = useCallback(
    (managerId?: number) => {
      if (!campaignId) {
        return false;
      }
      return isOfflineSyncDone(campaignId, managerId);
    },
    [campaignId]
  );

  const clearOfflineData = useCallback(async () => {
    return new Promise<void>((resolve) => {
      presentAlert({
        header: t('common:sync_job.clear_cache_title'),
        message: t('common:sync_job.clear_cache_message'),
        buttons: [
          {
            text: t('common:cancel'),
            role: 'cancel',
            cssClass: classes.cancelButton,
            handler: () => resolve(),
          },
          {
            text: t('common:confirm'),
            role: 'confirm',
            handler: async () => {
              await clearOfflineCache({ queryClient, reason: 'user' });
              await refreshJob();
              resolve();
            },
          },
        ],
      });
    });
  }, [presentAlert, queryClient, refreshJob, t]);

  return {
    startAutoSync,
    startSyncWithImages,
    startSyncWithParams,
    pauseJob,
    resumeJob,
    cancelJob,
    clearOfflineData,
    refreshJob,
    job,
    progress,
    isSettingUpCache: isRunning,
    loadingMessage: loadingMessage ?? getPhaseMessage(),
    setLoadingMessage,
    isScopeAlreadySynced,
  };
};
